import { CircularProgress, Stack } from '@mui/material'
import { FC, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useEngagement, useEngagementActivities, useLocation, useLocationCameras } from '../../../../api/apiQueries'
import { useSidePanelOutletContext } from '../../../../hooks/useSidePanelOutletContext'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { useErtRecordingData } from '../ErtQA/useErtRecordingData'
import { computeLiveStreamFacts, computeMainCanvasLiveStreamFacts, orderTimelineFragments } from './customerCareTimelineUtils'
import { buildTimelineFragments } from './timelineFragmentsUtils'
import { AccountDetails } from './AccountDetails'
import { EngagementDetails } from './EngagementDetails'
import { Timeline } from './Timeline'
import { RECORDING_ERROR_TOAST_TEXT } from './ertCustomerCareUtils'

export const ErtCustomerCare: FC = () => {
  const { sid: locationId, engagementId } = useParams()
  const { setSidePanelAddOn } = useSidePanelOutletContext()
  const { toast } = useSnackbar()

  const engagementQuery = useEngagement(engagementId)
  const engagement = engagementQuery.data
  const activitiesQuery = useEngagementActivities(engagementId)
  const activities = activitiesQuery.data
  const camerasQuery = useLocationCameras(locationId)
  const cameras = camerasQuery.data
  const locationQuery = useLocation(locationId)
  const location = locationQuery.data
  const {
    processedEvents,
    isRecordingsLoading,
    error: recordingError
  } = useErtRecordingData({
    id: engagementId,
    locationId,
    includeVideo: false,
    activities,
    activitiesFailed: activitiesQuery.isError
  })

  // Waiting on the location keeps the timeline from rendering rows in the browser's local zone before the
  // engagement's real timezone arrives (formatTimelineTime falls back silently when timezone is undefined).
  //
  // A recording-data error shouldn't block the rest of the timeline, which is mostly built from
  // activities/engagement data rather than the rrweb recording - so it doesn't gate isLoading once set.
  const isLoading =
    engagementQuery.isLoading ||
    activitiesQuery.isLoading ||
    locationQuery.isLoading ||
    camerasQuery.isLoading ||
    (isRecordingsLoading && !recordingError)

  const rrwebSummary = useMemo(
    () => processedEvents && activities && computeLiveStreamFacts(processedEvents, activities),
    [processedEvents, activities]
  )

  const mainCanvasLiveStream = useMemo(() => processedEvents && computeMainCanvasLiveStreamFacts(processedEvents), [processedEvents])

  // Built once so the Timeline rows and the Account Details handle time are derived from the same data.
  const timelineFragments = useMemo(
    () => (engagement && activities ? orderTimelineFragments(buildTimelineFragments(engagement, activities, cameras, mainCanvasLiveStream)) : []),
    [engagement, activities, cameras, mainCanvasLiveStream]
  )

  // No Customer Care side panel - clear whatever the previously active mode left behind.
  useEffect(() => {
    setSidePanelAddOn(null)
  }, [setSidePanelAddOn])

  // Some recordingError causes (e.g. the audio segments failing to assemble) happen after processedEvents is
  // already set, so the live-stream rows still render fine - this toast is only accurate when they're actually
  // left out.
  useEffect(() => {
    if (recordingError && !processedEvents) {
      toast(<span data-testid="recording-error-toast">{RECORDING_ERROR_TOAST_TEXT}</span>, { variant: 'error' })
    }
  }, [recordingError, processedEvents, toast])

  return isLoading ? (
    <Stack data-testid="ert-loading-spinner" sx={{ justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Stack>
  ) : (
    <Stack data-testid="ert-customer-care" direction="column" spacing="var(--spacing-s)">
      <AccountDetails location={location} engagement={engagement} cameras={cameras} timelineFragments={timelineFragments} activities={activities} />
      <Timeline engagement={engagement} activities={activities} fragments={timelineFragments} timezone={location?.address?.timeZone} />
      <EngagementDetails
        engagement={engagement}
        activities={activities}
        cameras={cameras}
        rrwebSummary={rrwebSummary}
        timezone={location?.address?.timeZone}
      />
    </Stack>
  )
}

ErtCustomerCare.displayName = 'ErtCustomerCare'
